import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  masterList,
  masterDetail,
  createMaster,
  updateMaster,
  deleteMaster,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";
import {
  IMasterCreatePayload,
  IMasterUpdatePayload,
  IMaster,
  MasterType,
} from "../../../types/api/master";
import { TResponseWithQData } from "../../../types/common";

export const MASTER_KEYS = {
  all: ["masters"] as const,
  lists: () => [...MASTER_KEYS.all, "list"] as const,
  list: (qData: QData) => [...MASTER_KEYS.lists(), qData] as const,
  details: () => [...MASTER_KEYS.all, "detail"] as const,
  detail: (id: string) => [...MASTER_KEYS.details(), id] as const,
};

export function useMasterList(
  qData: QData & { type?: MasterType; search?: string },
  options?: any
) {
  const [extraQuery, setExtraQuery] = useState<Record<string, any>>({});

  const mergedQData = React.useMemo(() => {
    return { ...qData, ...extraQuery };
  }, [qData, extraQuery]);

  const query = useQuery<TResponseWithQData<IMaster[]>>({
    queryKey: MASTER_KEYS.list(mergedQData),
    queryFn: async () => {
      const { data } = await masterList(mergedQData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = React.useCallback((newQData: typeof qData) => {
    setExtraQuery(newQData);
  }, []);

  return { ...query, refetchWithQuery };
}

export function useMasterDetail(id: string, options?: any) {
  return useQuery({
    queryKey: MASTER_KEYS.detail(id),
    queryFn: async () => {
      const { data } = await masterDetail(id);
      return data;
    },
    enabled: !!id,
    ...options,
  });
}

export function useMasterCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { type?: MasterType; search?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMaster,
    onSuccess: (res) => {
      if (qData) {
        queryClient.setQueryData(
          MASTER_KEYS.list(qData),
          (old: TResponseWithQData<IMaster[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: [res.data.data, ...(old.data || [])],
              extra: {
                ...old.extra,
                total: (old.extra?.total || 0) + 1,
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: MASTER_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}

export function useMasterUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { type?: MasterType; search?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMaster,
    onSuccess: (res) => {
      const updatedMaster = res.data.data;
      
      // Update lists
      if (qData) {
        queryClient.setQueryData(
          MASTER_KEYS.list(qData),
          (old: TResponseWithQData<IMaster[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.map((item) =>
                item._id === updatedMaster._id ? updatedMaster : item
              ),
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: MASTER_KEYS.lists() });
      }

      // Update detail
      queryClient.setQueryData(MASTER_KEYS.detail(updatedMaster._id), (old: any) => {
        if (!old) return old;
        return { ...old, data: updatedMaster };
      });

      onSuccess?.();
    },
    onError,
  });
}

export function useMasterDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { type?: MasterType; search?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMaster,
    onSuccess: (_, deletedId) => {
      if (qData) {
        queryClient.setQueryData(
          MASTER_KEYS.list(qData),
          (old: TResponseWithQData<IMaster[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.filter((item) => item._id !== deletedId),
              extra: {
                ...old.extra,
                total: Math.max(0, (old.extra?.total || 1) - 1),
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: MASTER_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}
